import React, { useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { formatDistanceToNow } from 'date-fns';
import sv from 'date-fns/locale/sv';

import {
  Avatar as MuiAvatar,
  Badge,
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Popover as MuiPopover,
  SvgIcon,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material';
import { Bell, AlertCircle, FileText, Calendar, DollarSign } from 'react-feather';

import useNotifications from '../../hooks/useNotifications';

const Popover = styled(MuiPopover)`
  .MuiPaper-root {
    width: 360px;
    max-height: 540px;
    ${(props) => props.theme.shadows[1]};
    border: 1px solid ${(props) => props.theme.palette.divider};
  }
`;

const Indicator = styled(Badge)`
  .MuiBadge-badge {
    background: ${(props) => props.theme.header.indicator.background};
    color: ${(props) => props.theme.palette.common.white};
  }
`;

const Avatar = styled(MuiAvatar)`
  background: ${(props) => props.theme.palette.primary.main};
`;

const Header = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(props) => props.theme.spacing(2)};
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

const ICON_FOR_TYPE = {
  nytt_lead: AlertCircle,
  faktureringsbeslut_vantar: FileText,
  faktura_obetald: DollarSign,
  mote_paminnelse: Calendar,
  abonnemang_forlangt: FileText,
  retention_deadline: AlertCircle,
};

const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: sv });
  } catch (err) {
    return '';
  }
};

const NotificationItem = ({ notif, onClick }) => {
  const Icon = ICON_FOR_TYPE[notif.type] || Bell;
  return (
    <ListItemButton divider onClick={onClick} alignItems="flex-start" sx={{ opacity: notif.is_read ? 0.6 : 1 }}>
      <ListItemAvatar>
        <Avatar>
          <SvgIcon fontSize="small">
            <Icon />
          </SvgIcon>
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={notif.title}
        primaryTypographyProps={{ variant: 'subtitle2', color: 'textPrimary' }}
        secondary={
          <>
            {notif.description && (
              <Typography variant="body2" color="text.secondary" component="span" display="block">
                {notif.description}
              </Typography>
            )}
            <Typography variant="caption" color="text.disabled" component="span">
              {formatRelative(notif.createdAt || notif.created_at)}
            </Typography>
          </>
        }
      />
    </ListItemButton>
  );
};

const NotificationsBell = () => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleClickItem = async (notif) => {
    if (!notif.is_read) {
      await markRead(notif.id);
    }
    if (notif.link_url) {
      handleClose();
      navigate(notif.link_url);
    }
  };

  return (
    <>
      <Tooltip title="Notifikationer">
        <IconButton color="inherit" ref={ref} onClick={handleOpen} size="large">
          <Indicator badgeContent={unreadCount} max={99} invisible={unreadCount === 0}>
            <Bell />
          </Indicator>
        </IconButton>
      </Tooltip>
      <Popover
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={open}
      >
        <Header>
          <Typography variant="subtitle1" color="textPrimary">
            {unreadCount > 0 ? `${unreadCount} olästa` : 'Inga olästa'}
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={markAllRead}>
              Markera alla lästa
            </Button>
          )}
        </Header>
        {notifications.length === 0 ? (
          <Box p={3} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              Inga notifikationer ännu.
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ maxHeight: 420, overflow: 'auto' }}>
            {notifications.slice(0, 25).map((notif) => (
              <NotificationItem key={notif.id} notif={notif} onClick={() => handleClickItem(notif)} />
            ))}
          </List>
        )}
        <Divider />
        <Box p={1} display="flex" justifyContent="center">
          <Button
            size="small"
            component={RouterLink}
            to="/admin/drift/notifikationer"
            onClick={handleClose}
          >
            Hantera notifikationsinställningar
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationsBell;
